import { QueryStatus, useQuery } from "@tanstack/react-query";
import { BarChart, XAxis, YAxis, Bar } from "recharts";
import { format, parseISO } from "date-fns";

import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { type ChartConfig, ChartContainer } from "@/components/ui/chart";
import { Skeleton } from "@/components/ui/skeleton";
import { LockedContent } from "@/components/layout/locked-content";

import { message, cn } from "@/lib/utils";
import { ConsumerResearchApiFactory } from "@/apis/brand-os/consumer-research";

type VolumeTrendRow = { day: string; volume: number };

function VolumeTrendComponent({ status, chartRows }: { status: QueryStatus; chartRows: VolumeTrendRow[] }) {
  const showLocked = !chartRows.length || chartRows.every((d) => d.volume === 0);

  switch (status) {
    case "pending":
      return <VolumeTrendSkeleton />;
    case "error":
      return <LockedContent message={message} className="flex-1 min-h-0" />;
    case "success":
      return showLocked ? <LockedContent message={message} className="flex-1 min-h-0" /> : <VolumeTrendChart data={chartRows} />;
  }
}

export function VolumeTrend() {
  const { data: weeklyVolumeData, status } = useQuery(ConsumerResearchApiFactory.Queries.OverviewWeeklyVolume());

  const chartRows = (weeklyVolumeData?.volume ?? []).map((item) => ({
    day: format(parseISO(item.date), "EEE"),
    volume: item.mention_count,
  }));

  return (
    <Card className="flex h-full min-h-0 flex-col">
      <CardHeader>
        <CardTitle>Weekly Volume</CardTitle>
      </CardHeader>
      <CardContent className="flex min-h-0 flex-1 flex-col">
        <VolumeTrendComponent status={status} chartRows={chartRows} />
      </CardContent>
    </Card>
  );
}

function VolumeTrendChart({ data }: { data: VolumeTrendRow[] }) {
  const maxValue = Math.max(...data.map((d) => d.volume));
  const domainMax = Math.ceil(maxValue / 10) * 10 || 10;

  const chartConfig = {
    volume: {
      label: "Volume",
      color: "var(--destructive)",
    },
  } satisfies ChartConfig;

  return (
    <ChartContainer config={chartConfig} className="h-full min-h-32 w-full flex-1">
      <BarChart accessibilityLayer data={data} margin={{ top: 8, right: 8, left: 0, bottom: 0 }}>
        <XAxis dataKey="day" tickLine={false} axisLine={false} tickMargin={8} tick={{ fill: "hsl(var(--muted-foreground))", fontSize: 12 }} />
        <YAxis hide domain={[0, domainMax]} />
        <Bar dataKey="volume" fill="var(--color-volume)" radius={[8, 8, 0, 0]} barSize={30} />
      </BarChart>
    </ChartContainer>
  );
}

function VolumeTrendSkeleton() {
  const heightClasses = ["h-[40%]", "h-[52%]", "h-[64%]", "h-[76%]"];
  return (
    <div className="flex min-h-28 flex-1 items-end gap-3">
      {Array.from({ length: 7 }, (_, index) => (
        <Skeleton key={index} className={cn("flex-1 rounded-t-lg", heightClasses[index % 4])} />
      ))}
    </div>
  );
}
